import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ConfirmDialog from "./ConfirmDialog";

const UnsavedChangesContext = createContext(null);

// Only forms inside an explicitly opted-in boundary participate in close protection.
export function useUnsavedChanges(isDirty) {
  const context = useContext(UnsavedChangesContext);
  const id = useRef(Symbol("form"));
  useLayoutEffect(() => {
    if (!context) return undefined;
    const key = id.current;
    context.dirtyForms.set(key, Boolean(isDirty));
    return () => context.dirtyForms.delete(key);
  }, [context, isDirty]);
  return context?.requestClose;
}

// Capture the editable values at the start of each edit session, not on refetch.
export function useDraftDirty(values, active) {
  const baseline = useRef(null);
  const wasActive = useRef(false);
  const serialized = JSON.stringify(values);
  if (!active || !wasActive.current) baseline.current = serialized;
  wasActive.current = active;
  return Boolean(active && serialized !== baseline.current);
}

export function DirtyState({ isDirty }) {
  useUnsavedChanges(isDirty);
  return null;
}

export default function UnsavedChangesBoundary({ onClose, itemName = "item", closeEvent, children }) {
  const dirtyForms = useRef(new Map());
  const [pendingClose, setPendingClose] = useState(null);
  const requestClose = useCallback((callback) => {
    const close = typeof callback === "function" ? callback : onClose;
    if ([...dirtyForms.current.values()].some(Boolean)) {
      setPendingClose(() => close);
    } else {
      close?.();
    }
  }, [onClose]);
  const context = useMemo(() => ({ dirtyForms: dirtyForms.current, requestClose }), [requestClose]);

  useEffect(() => {
    if (!closeEvent) return undefined;
    window.addEventListener(closeEvent, requestClose);
    return () => window.removeEventListener(closeEvent, requestClose);
  }, [closeEvent, requestClose]);

  return (
    <UnsavedChangesContext.Provider value={context}>
      {children(requestClose)}
      {pendingClose && createPortal(
        <ConfirmDialog
          open
          title="Unsaved changes"
          message={"This " + itemName + " has not been saved. Do you want to continue editing or discard your changes?"}
          cancelLabel="Continue editing"
          confirmLabel="Discard changes"
          onCancel={() => setPendingClose(null)}
          onConfirm={() => {
            setPendingClose(null);
            pendingClose();
          }}
          backdropStyle={{ zIndex: 10000 }}
        />,
        document.body
      )}
    </UnsavedChangesContext.Provider>
  );
}
